import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import MyLoader from "../Spinner/MyLoader";
import SessionManager from "../Utils/SessionManager";
import "../css/Admin_css/admin-customize.css";
import myToast from "../Utils/MyToast";
import COLORS from "../resources/colors/COLORS";
import BackButton from "../ChildComponent/BackButton";
import icon_delete from "../resources/icons/icons_delete_32.png";
import SelectClass from "../ChildComponent/SelectClass";
import { Modal, Button } from "react-bootstrap";

// ============================================================================
// CONSTANTS
// ============================================================================

const API_ENDPOINTS = {
  GET_REPORTS: "getAllStudentAttendanceReport.php",
  DELETE_REPORT: "DeleteStudentAttendanceReport.php",
  GET_WORKING_DAYS: "getSchoolWorkingDays.php",
  BATCH_UPSERT: "AddStudentAttendanceReportBatch.php",
};

const STYLES = {
  header: {
    margin: 0,
    fontSize: "20px",
    fontWeight: "700",
    color: COLORS.primary_color_shade5,
  },
  subHeader: {
    margin: "2px 0 0 0",
    fontSize: "11px",
    color: "rgba(255, 255, 255, 0.9)",
    fontWeight: "400",
  },
  label: {
    fontSize: "12px",
    fontWeight: "600",
    color: "#374151",
    marginBottom: "4px",
    display: "block",
  },
  input: {
    width: "100%",
    padding: "10px 14px",
    fontSize: "13px",
    borderRadius: "10px",
    border: "2px solid #e5e7eb",
    background: "#ffffff",
    fontWeight: "600",
    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
    outline: "none",
  },
  button: {
    padding: "8px 16px",
    fontSize: "12px",
    fontWeight: "700",
    borderRadius: "10px",
    border: "none",
    cursor: "pointer",
    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
    display: "flex",
    alignItems: "center",
    gap: "6px",
  },
  emptyState: {
    textAlign: "center",
    padding: "40px 20px",
    color: "#6b7280",
  },
  emptyStateIcon: {
    width: "60px",
    height: "60px",
    borderRadius: "50%",
    background: "linear-gradient(135deg, #f3f4f6 0%, #e5e7eb 100%)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    margin: "0 auto 12px",
    fontSize: "28px",
    color: "#9ca3af",
  },
};

const buildApiUrl = (fn) =>
  `${process.env.REACT_APP_SERVER_NAME}${process.env.REACT_APP_TARGET_DIR}${fn}`;

// ============================================================================
// API FUNCTIONS
// ============================================================================

const fetchAttendanceReports = async (sessionId, schoolCode, classId = null, sectionId = null) => {
  const url = buildApiUrl(API_ENDPOINTS.GET_REPORTS);
  const params = {
    session_id: sessionId,
    school_code: schoolCode,
  };
  if (classId) params.class_id = classId;
  if (sectionId && sectionId !== "No") params.section_id = sectionId;
  
  try {
    const res = await axios.get(url, { params });
    if (res.data?.api_status === "OK") {
      return { success: true, data: res.data?.data || [] };
    }
    return { success: false, message: res.data?.api_remark || "Failed to load reports" };
  } catch (error) {
    console.error("Error fetching attendance reports:", error);
    return { success: false, message: error.message || "Error loading reports" };
  }
};

const fetchSchoolWorkingDays = async (sessionId, schoolCode) => {
  const url = buildApiUrl(API_ENDPOINTS.GET_WORKING_DAYS);
  const params = {
    session_id: sessionId,
    school_code: schoolCode,
  };
  try {
    const res = await axios.get(url, { params });
    if (res.data?.api_status === "OK") {
      const list = Array.isArray(res.data?.data) ? res.data.data : [];
      const normalizedList = list
        .map((row) => ({
          month: String(row.month || "").trim(),
          month2: row.month2 ? String(row.month2).trim() : "",
          working_days: Number(row.working_days ?? row.workingDays ?? row.days ?? 0),
        }))
        .filter((r) => r.month);
      const map = {};
      normalizedList.forEach((row) => {
        map[row.month] = row.working_days;
      });
      return { success: true, data: map, list: normalizedList };
    }
    return { success: false, message: res.data?.api_remark || "Failed to load working days" };
  } catch {
    return { success: false, message: "Error loading working days" };
  }
};

const deleteAttendanceReport = async (staffId, id, schoolCode) => {
  const url = buildApiUrl(API_ENDPOINTS.DELETE_REPORT);
  const params = { staff_id: staffId, id, school_code: schoolCode };
  try {
    const res = await axios.post(url, new URLSearchParams(params), {
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
    });
    if (res.data?.api_status === "OK") {
      return { success: true, message: res.data?.api_remark || "Report deleted successfully" };
    }
    return { success: false, message: res.data?.api_remark || "Failed to delete report" };
  } catch {
    return { success: false, message: "Error deleting report" };
  }
};

const batchUpsertAttendanceReports = async (staffId, attendanceReports, schoolCode) => {
  const url = buildApiUrl(API_ENDPOINTS.BATCH_UPSERT);
  const params = new URLSearchParams({
    staff_id: staffId,
    attendance_reports: JSON.stringify(attendanceReports),
    school_code: schoolCode,
  });
  try {
    const res = await axios.post(url, params, {
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
    });
    if (res.data?.api_status === "OK") {
      return { 
        success: true, 
        message: res.data?.api_remark || "Attendance reports saved successfully",
        successCount: res.data?.data?.successCount || 0,
        errorCount: res.data?.data?.errorCount || 0,
        errors: res.data?.errors || []
      };
    }
    return { 
      success: false, 
      message: res.data?.api_remark || "Failed to save attendance reports",
      errors: res.data?.errors || []
    };
  } catch (error) {
    console.error("Error batch upserting attendance reports:", error.response?.data || error.message);
    return { 
      success: false, 
      message: error.response?.data?.api_remark || error.message || "Error saving attendance reports" 
    };
  }
};

// ============================================================================
// UI COMPONENTS
// ============================================================================

const AttendanceInputCell = ({ value, onChange, placeholder = "Present", isExistingRecord = false, isDefaultInput = false }) => {
  // Determine border color: light green for existing records, orange for default inputs
  const getBorderColor = () => {
    if (isExistingRecord) return "#34d399"; // Light green
    if (isDefaultInput) return "#f59e0b"; // Orange
    return "#e5e7eb"; // Default gray
  };

  const getBoxShadow = () => {
    if (isExistingRecord) return "0 0 4px rgba(52, 211, 153, 0.3), 0 0 6px rgba(52, 211, 153, 0.2)"; // Glowing light green
    if (isDefaultInput) return "0 0 4px rgba(245, 158, 11, 0.3), 0 0 6px rgba(245, 158, 11, 0.2)"; // Glowing orange
    return "none";
  };

  const borderColor = getBorderColor();
  const boxShadow = getBoxShadow();

  return (
    <input
      type="number"
      min="0"
      value={value || ""}
      onChange={onChange}
      placeholder={placeholder}
      style={{
        ...STYLES.input,
        borderColor: borderColor,
        borderWidth: isExistingRecord || isDefaultInput ? "2px" : "2px",
        boxShadow: boxShadow,
      }}
      onFocus={(e) => {
        e.target.style.borderColor = COLORS.primary_color_shade5 || "#3b82f6";
        e.target.style.boxShadow = `0 0 0 4px ${(COLORS.primary_color_shade5 || "#3b82f6")}20`;
        e.target.style.transform = "scale(1.02)";
        e.target.style.background = "#f8fafc";
      }}
      onBlur={(e) => {
        e.target.style.borderColor = borderColor;
        e.target.style.boxShadow = boxShadow;
        e.target.style.transform = "scale(1)";
        e.target.style.background = "#ffffff";
      }}
    />
  );
};

const EmptyState = ({ message, icon = "fa-inbox" }) => (
  <div style={STYLES.emptyState}>
    <div style={STYLES.emptyStateIcon}>
      <i className={`fa ${icon}`}></i>
    </div>
    <p style={{ margin: 0, fontSize: "14px", fontWeight: "600", color: "#111827" }}>
      {message}
    </p>
  </div>
);

// ============================================================================
// MAIN COMPONENT
// ============================================================================

const ManageManualAttendance = () => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savingAll, setSavingAll] = useState(false);
  const [savingStudents, setSavingStudents] = useState({});
  const [sessionId, setSessionId] = useState("");
  const [reports, setReports] = useState([]);
  const [LoadClass, setLoadClass] = useState(true);
  const [SELECTED_CLASS_POSITION, setSELECTED_CLASS_POSITION] = useState(0);
  const [selectedClassId, setSelectedClassId] = useState("");
  const [selectedSectionId, setSelectedSectionId] = useState("");
  const [workingDaysMap, setWorkingDaysMap] = useState({});
  const [workingDaysList, setWorkingDaysList] = useState([]);
  const [selectedMonths, setSelectedMonths] = useState([]);
  const [presentInputs, setPresentInputs] = useState({});
  const [showAttendancePreview, setShowAttendancePreview] = useState(false);
  const [setWorkingDaysAsDefault, setSetWorkingDaysAsDefault] = useState(false);
  const [defaultInputs, setDefaultInputs] = useState({}); // Track which inputs are defaults: {studentId: {month: true}}

  useEffect(() => {
    window.scrollTo(0, 0);
    const session = SessionManager.getCurrentSession && SessionManager.getCurrentSession();
    if (session && session[0]) {
      setSessionId(session[0].session_id);
    }
  }, []);

  const schoolCode = SessionManager.getSchoolCode();
  const staffDetails = SessionManager.getStaffDetails();
  const staffId = staffDetails?.staff_id || "";

  const fetchReportsOnly = useCallback(async () => {
    if (!sessionId || !schoolCode) return;
    
    try {
      const reportsRes = await fetchAttendanceReports(
        sessionId,
        schoolCode,
        selectedClassId || null,
        selectedSectionId || null
      );
      if (reportsRes.success) {
        const rows = reportsRes.data || [];
        setReports(rows);
        const initialPresent = {};
        rows.forEach((r) => {
          const sid = r.student_id || r.id;
          const m = (r.month || "").trim();
          if (sid && m) {
            if (!initialPresent[sid]) initialPresent[sid] = {};
            initialPresent[sid][m] = r.present ?? "";
          }
        });
        setPresentInputs(initialPresent);
        
        // Clear default flags for existing records (they should show green border, not orange)
        setDefaultInputs((prev) => {
          const updated = { ...prev };
          rows.forEach((r) => {
            const sid = r.student_id || r.id;
            const m = (r.month || "").trim();
            if (sid && m && r.present !== null && r.present !== undefined && r.present !== "") {
              if (updated[sid] && updated[sid][m]) {
                const newStudentDefaults = { ...updated[sid] };
                delete newStudentDefaults[m];
                if (Object.keys(newStudentDefaults).length === 0) {
                  const newDefaults = { ...updated };
                  delete newDefaults[sid];
                  return newDefaults;
                }
                updated[sid] = newStudentDefaults;
              }
            }
          });
          return updated;
        });
      } else {
        console.error("Failed to load reports:", reportsRes.message);
        myToast.notify_warning(reportsRes.message);
        setReports([]);
      }
    } catch (error) {
      console.error("Error fetching reports:", error);
      myToast.notify_error("Failed to load reports");
    }
  }, [sessionId, schoolCode, selectedClassId, selectedSectionId]);

  const fetchData = useCallback(async () => {
    if (!sessionId || !schoolCode) return;
    
    setLoading(true);
    try {
      const wdRes = await fetchSchoolWorkingDays(sessionId, schoolCode);
      if (wdRes.success) {
        const listArr = Array.isArray(wdRes.list) ? wdRes.list : [];
        setWorkingDaysMap(wdRes.data || {});
        setWorkingDaysList(listArr);
        const firstMonth = (listArr.find((r) => r.month) || {}).month;
        setSelectedMonths(firstMonth ? [firstMonth] : []);
      } else {
        setWorkingDaysMap({});
        setWorkingDaysList([]);
        setSelectedMonths([]);
      }

      const reportsRes = await fetchAttendanceReports(
        sessionId,
        schoolCode,
        selectedClassId || null,
        selectedSectionId || null
      );
      if (reportsRes.success) {
        const rows = reportsRes.data || [];
        setReports(rows);
        const initialPresent = {};
        rows.forEach((r) => {
          const sid = r.student_id || r.id;
          const m = (r.month || "").trim();
          if (sid && m) {
            if (!initialPresent[sid]) initialPresent[sid] = {};
            initialPresent[sid][m] = r.present ?? "";
          }
        });
        setPresentInputs(initialPresent);
        
        // Clear default flags for existing records (they should show green border, not orange)
        setDefaultInputs((prev) => {
          const updated = { ...prev };
          rows.forEach((r) => {
            const sid = r.student_id || r.id;
            const m = (r.month || "").trim();
            if (sid && m && r.present !== null && r.present !== undefined && r.present !== "") {
              if (updated[sid] && updated[sid][m]) {
                const newStudentDefaults = { ...updated[sid] };
                delete newStudentDefaults[m];
                if (Object.keys(newStudentDefaults).length === 0) {
                  const newDefaults = { ...updated };
                  delete newDefaults[sid];
                  return newDefaults;
                }
                updated[sid] = newStudentDefaults;
              }
            }
          });
          return updated;
        });
      } else {
        console.error("Failed to load reports:", reportsRes.message);
        myToast.notify_warning(reportsRes.message);
        setReports([]);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      myToast.notify_error("Failed to load data");
    } finally {
      setLoading(false);
    }
  }, [sessionId, schoolCode, selectedClassId, selectedSectionId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Effect to set working days as default when checkbox is checked
  useEffect(() => {
    if (!setWorkingDaysAsDefault || selectedMonths.length === 0 || workingDaysList.length === 0) {
      return;
    }

    // Get unique students from reports or use filteredReports if available
    const studentMap = new Map();
    (reports || []).forEach((report) => {
      const studentId = report.student_id;
      if (studentId && !studentMap.has(studentId)) {
        studentMap.set(studentId, report);
      }
    });
    const uniqueStudents = Array.from(studentMap.values());

    if (uniqueStudents.length === 0) {
      return;
    }

    // Update present inputs with working days for months without existing records
    setPresentInputs((prev) => {
      const updated = { ...prev };
      const defaultTracking = {};
      
      uniqueStudents.forEach((student) => {
        const studentId = student.student_id;
        if (!studentId) return;

        selectedMonths.forEach((month) => {
          // Check if there's an existing report for this student and month with a present value
          const existingReport = (reports || []).find(
            (r) => r.student_id === studentId && (r.month || "").trim() === month.trim()
          );

          // Only set default if no existing record OR existing record has no present value
          const hasExistingValue = existingReport && 
            existingReport.present !== null && 
            existingReport.present !== undefined && 
            existingReport.present !== "";

          if (!hasExistingValue) {
            // Get working days for this month
            const monthInfo = workingDaysList.find((r) => (r.month || "").trim() === month.trim());
            const workingDays = monthInfo?.working_days || 0;

            if (workingDays > 0) {
              if (!updated[studentId]) {
                updated[studentId] = {};
              }
              // Set working days as default (only if checkbox is checked)
              updated[studentId][month] = String(workingDays);
              
              // Track this as a default input
              if (!defaultTracking[studentId]) {
                defaultTracking[studentId] = {};
              }
              defaultTracking[studentId][month] = true;
            }
          }
        });
      });

      // Update default inputs tracking
      setDefaultInputs((prevDefaults) => {
        const updatedDefaults = { ...prevDefaults };
        Object.keys(defaultTracking).forEach((studentId) => {
          if (!updatedDefaults[studentId]) {
            updatedDefaults[studentId] = {};
          }
          Object.assign(updatedDefaults[studentId], defaultTracking[studentId]);
        });
        return updatedDefaults;
      });

      return updated;
    });
  }, [setWorkingDaysAsDefault, selectedMonths, workingDaysList, reports]);

  const CallBackClassSelect = (
    class_id,
    section_id,
    selected_session_id,
    selected_position
  ) => {
    if (typeof selected_position !== "undefined" && selected_position !== null) {
      setSELECTED_CLASS_POSITION(Number(selected_position) || 0);
    }
    if (class_id) setSelectedClassId(class_id); else setSelectedClassId("");
    if (section_id) setSelectedSectionId(section_id); else setSelectedSectionId("");
    if (selected_session_id && selected_session_id !== sessionId) {
      setSessionId(selected_session_id);
    }
  };

  const handleDelete = async (report) => {
    if (!window.confirm(`Are you sure you want to delete attendance report for ${report.student_name || report.student_id}?`)) {
      return;
    }

    setSaving(true);
    try {
      const result = await deleteAttendanceReport(staffId, report.id, schoolCode);
      if (result.success) {
        myToast.notify_success(result.message);
        fetchReportsOnly();
      } else {
        myToast.notify_warning(result.message);
      }
    } catch (error) {
      myToast.notify_error("Failed to delete report");
    } finally {
      setSaving(false);
    }
  };

  const handleMonthToggle = (month) => {
    setSelectedMonths((prev) => {
      if (prev.includes(month)) {
        return prev.filter((m) => m !== month);
      } else {
        return [...prev, month];
      }
    });
  };

  const handleSelectAllMonths = () => {
    const all = (workingDaysList || []).map((r) => r.month).filter(Boolean);
    setSelectedMonths(all);
  };

  const handleClearMonths = () => {
    setSelectedMonths([]);
  };

  const handlePresentInputChange = (studentId, month, value) => {
    setPresentInputs((prev) => ({
      ...prev,
      [studentId]: {
        ...(prev[studentId] || {}),
        [month]: value
      }
    }));
    // Clear default flag when user manually changes the value
    setDefaultInputs((prev) => {
      const updated = { ...prev };
      if (updated[studentId] && updated[studentId][month]) {
        const newStudentDefaults = { ...updated[studentId] };
        delete newStudentDefaults[month];
        if (Object.keys(newStudentDefaults).length === 0) {
          const newDefaults = { ...updated };
          delete newDefaults[studentId];
          return newDefaults;
        }
        return { ...updated, [studentId]: newStudentDefaults };
      }
      return updated;
    });
  };

  const getHeaderLabelForMonth = useCallback(
    (monthName) => {
      const row = (workingDaysList || []).find((r) => String(r.month).trim() === String(monthName).trim());
      if (row) {
        const monthLabel = row.month2 ? String(row.month2).trim() : monthName;
        const workingDays = row.working_days || 0;
        return `${monthLabel} (${workingDays} days)`;
      }
      return monthName;
    },
    [workingDaysList]
  );

  const getMonth2ForMonth = useCallback(
    (monthName) => {
      const row = (workingDaysList || []).find((r) => String(r.month).trim() === String(monthName).trim());
      return row?.month2 || "";
    },
    [workingDaysList]
  );

  // Abbreviate month name (e.g., "September" -> "Sept", "January" -> "Jan")
  const abbreviateMonth = useCallback((monthName) => {
    if (!monthName) return "";
    const month = String(monthName).trim();
    const monthAbbreviations = {
      "January": "Jan",
      "February": "Feb",
      "March": "Mar",
      "April": "Apr",
      "May": "May",
      "June": "Jun",
      "July": "Jul",
      "August": "Aug",
      "September": "Sept",
      "October": "Oct",
      "November": "Nov",
      "December": "Dec",
    };
    return monthAbbreviations[month] || month.substring(0, 4);
  }, []);

  const handleSaveRow = async (report) => {
    const studentId = report.student_id;
    if (!studentId || !selectedClassId || !sessionId) {
      myToast.notify_warning("Missing required information");
      return;
    }

    if (!staffId || !schoolCode) {
      myToast.notify_warning("Missing staff or school information");
      return;
    }

    setSavingStudents((prev) => ({ ...prev, [studentId]: true }));

    try {
      // Prepare batch data for all selected months
      const batchReports = selectedMonths.map((month) => {
        const present = presentInputs[studentId]?.[month] || "0";
        const month2 = getMonth2ForMonth(month);
        
        // Check if report exists for this student and month
        const existingReport = reports.find(
          (r) => r.student_id === studentId && (r.month || "").trim() === month.trim() && r.id
        );
        
        return {
          student_id: studentId,
          class_id: selectedClassId,
          section_id: selectedSectionId || "",
          session_id: sessionId,
          month: month,
          month2: month2 || "",
          present: present,
          id: existingReport?.id || null
        };
      });

      // Single batch API call for all months
      const result = await batchUpsertAttendanceReports(staffId, batchReports, schoolCode);

      if (result.success) {
        const successCount = result.successCount || batchReports.length;
        myToast.notify_success(`Saved ${successCount} attendance record(s)`);
        
        // Clear default flags for saved months since they're now existing records
        setDefaultInputs((prev) => {
          const updated = { ...prev };
          if (updated[studentId]) {
            const newStudentDefaults = { ...updated[studentId] };
            selectedMonths.forEach((month) => {
              delete newStudentDefaults[month];
            });
            if (Object.keys(newStudentDefaults).length === 0) {
              const newDefaults = { ...updated };
              delete newDefaults[studentId];
              return newDefaults;
            }
            return { ...updated, [studentId]: newStudentDefaults };
          }
          return updated;
        });
        
        fetchReportsOnly();
      } else {
        console.error("Batch save failed:", result.message);
        myToast.notify_warning(result.message || "Failed to save attendance records");
      }
    } catch (error) {
      console.error("Error saving attendance:", error);
      myToast.notify_error(`Failed to save attendance: ${error.message || "Unknown error"}`);
    } finally {
      setSavingStudents((prev) => {
        const newState = { ...prev };
        delete newState[studentId];
        return newState;
      });
    }
  };

  const handleSaveAll = async () => {
    if (!selectedClassId || !sessionId || selectedMonths.length === 0) {
      myToast.notify_warning("Please select class and at least one month");
      return;
    }

    setSavingAll(true);

    try {
      // Get unique students (since reports now contains all month records)
      const studentMap = new Map();
      reports.forEach((report) => {
        const studentId = report.student_id;
        if (studentId && !studentMap.has(studentId)) {
          studentMap.set(studentId, report);
        }
      });
      const uniqueStudents = Array.from(studentMap.values());

      // Prepare batch data for all students and all selected months
      const batchReports = [];
      uniqueStudents.forEach((report) => {
        const studentId = report.student_id;
        if (!studentId) return;

        selectedMonths.forEach((month) => {
          const present = presentInputs[studentId]?.[month] || "0";
          const month2 = getMonth2ForMonth(month);
          
          // Find existing report for this student and month
          const existingReport = reports.find(
            (r) => r.student_id === studentId && (r.month || "").trim() === month.trim() && r.id
          );
          
          batchReports.push({
            student_id: studentId,
            class_id: selectedClassId,
            section_id: selectedSectionId || "",
            session_id: sessionId,
            month: month,
            month2: month2 || "",
            present: present,
            id: existingReport?.id || null
          });
        });
      });

      // Single batch API call for all students and all months
      const result = await batchUpsertAttendanceReports(staffId, batchReports, schoolCode);

      if (result.success) {
        const successCount = result.successCount || batchReports.length;
        myToast.notify_success(`Saved ${successCount} attendance record(s)`);
        
        // Clear default flags for all saved students and months since they're now existing records
        setDefaultInputs((prev) => {
          const updated = { ...prev };
          uniqueStudents.forEach((report) => {
            const studentId = report.student_id;
            if (studentId && updated[studentId]) {
              const newStudentDefaults = { ...updated[studentId] };
              selectedMonths.forEach((month) => {
                delete newStudentDefaults[month];
              });
              if (Object.keys(newStudentDefaults).length === 0) {
                delete updated[studentId];
              } else {
                updated[studentId] = newStudentDefaults;
              }
            }
          });
          return updated;
        });
        
        fetchReportsOnly();
      } else {
        console.error("Batch save failed:", result.message);
        myToast.notify_warning(result.message || "Failed to save attendance records");
        if (result.errors && result.errors.length > 0) {
          console.error("Errors:", result.errors);
        }
      }
    } catch (error) {
      myToast.notify_error("Failed to save attendance");
    } finally {
      setSavingAll(false);
    }
  };

  // Group reports by student_id to get unique students
  // Since API now returns all month records, we need to deduplicate by student
  const studentMap = new Map();
  reports.forEach((report) => {
    const studentId = report.student_id;
    if (studentId && !studentMap.has(studentId)) {
      // Store the first report for this student (contains student info)
      studentMap.set(studentId, report);
    }
  });
  const filteredReports = Array.from(studentMap.values());

  // Calculate completed and not completed counts for Attendance Preview
  const calculateAttendanceStats = useCallback(() => {
    const allMonths = workingDaysList.map((r) => r.month).filter(Boolean);
    let completedCount = 0;
    let notCompletedCount = 0;
    const totalExpected = filteredReports.length * allMonths.length;

    filteredReports.forEach((student) => {
      const studentId = student.student_id;
      allMonths.forEach((month) => {
        const report = reports.find(
          (r) => r.student_id === studentId && String(r.month).trim() === String(month).trim()
        );
        if (report && report.present !== null && report.present !== undefined && report.present !== "") {
          completedCount++;
        } else {
          notCompletedCount++;
        }
      });
    });

    return { completedCount, notCompletedCount, totalExpected };
  }, [filteredReports, reports, workingDaysList]);

  // Get all attendance data grouped by student for preview modal
  const getAttendancePreviewData = useCallback(() => {
    const allMonths = workingDaysList.map((r) => r.month).filter(Boolean);
    const previewData = filteredReports.map((student) => {
      const studentId = student.student_id;
      const monthData = {};
      allMonths.forEach((month) => {
        const report = reports.find(
          (r) => r.student_id === studentId && String(r.month).trim() === String(month).trim()
        );
        monthData[month] = {
          present: report?.present ?? "",
          working_days: report?.working_days ?? (workingDaysMap[month]?.working_days || 0),
          month2: report?.month2 || workingDaysMap[month]?.month2 || "",
          isCompleted: report && report.present !== null && report.present !== undefined && report.present !== "",
        };
      });
      return {
        student_id: studentId,
        student_name: student.student_name || student.student_id || "N/A",
        student_roll_no: student.student_roll_no || "-",
        months: monthData,
      };
    });
    return previewData;
  }, [filteredReports, reports, workingDaysList, workingDaysMap]);

  const token = localStorage.getItem("token");
  const loggedIn = token != null;

  if (!loggedIn) {
    return <div>Please login to continue</div>;
  }

  if (loading && sessionId && reports.length === 0) {
    return <MyLoader />;
  }
  const SaveAllButton = ({ position = "top" }) => (
    <div style={{ 
      display: "flex", 
      justifyContent: "center",
      marginBottom: position === "top" ? "16px" : "0",
      marginTop: position === "bottom" ? "16px" : "0",
      padding: "16px",
      background: "linear-gradient(135deg, #f8fafc 0%, #ffffff 100%)",
      borderRadius: "12px",
      border: "2px solid #e5e7eb",
      boxShadow: "0 2px 8px rgba(0, 0, 0, 0.05)",
    }}>
      <button
        onClick={handleSaveAll}
        disabled={savingAll || selectedMonths.length === 0}
        style={{
          ...STYLES.button,
          padding: "12px 24px",
          fontSize: "14px",
          fontWeight: "700",
          borderRadius: "12px",
          background: savingAll || selectedMonths.length === 0
            ? "#9ca3af"
            : "linear-gradient(135deg, #10b981 0%, #059669 100%)",
          color: "#fff",
          boxShadow: savingAll || selectedMonths.length === 0 
            ? "none" 
            : "0 4px 12px rgba(16, 185, 129, 0.35)",
          minWidth: "160px",
          justifyContent: "center",
        }}
        onMouseEnter={(e) => {
          if (!savingAll && selectedMonths.length > 0) {
            e.target.style.transform = "translateY(-3px) scale(1.02)";
            e.target.style.boxShadow = "0 8px 20px rgba(16, 185, 129, 0.45)";
          }
        }}
        onMouseLeave={(e) => {
          if (!savingAll && selectedMonths.length > 0) {
            e.target.style.transform = "translateY(0) scale(1)";
            e.target.style.boxShadow = "0 4px 12px rgba(16, 185, 129, 0.35)";
          }
        }}
      >
        {savingAll ? (
          <>
            <i className="fa fa-spinner fa-spin"></i>
            Saving All...
          </>
        ) : (
          <>
            <i className="fa fa-check"></i>
            Save All Attendance
          </>
        )}
      </button>
    </div>
  );

  return (
    <div className="content" style={{ background: COLORS.whitesmoke }}>
      <div
        className="content-header"
        style={{ background: COLORS.primary_color_shade6 }}
      >
        <div
          className="leftside-content-header"
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: "10px",
          }}
        >
          <ul className="breadcrumbs" style={{ margin: 0 }}>
            <li>
              <i className="fa fa-home" aria-hidden="true" />
              <a href="#"> Manage Manual Attendance </a>
            </li>
          </ul>
          <BackButton
            style={{
              height: "28px",
              fontSize: "11px",
              padding: "2px 6px",
              marginTop: "0px",
            }}
          />
        </div>
      </div>

      <div
        className="bg_page_white_rounded box_shadow"
        style={{
          "--mMargin": "10px",
          marginTop: "20px",
          padding: "24px",
          borderRadius: "16px",
        }}
      >
        {LoadClass && (
          <div style={{ marginBottom: "16px" }}>
            <SelectClass
              value={LoadClass}
              action={CallBackClassSelect}
              selected_position={SELECTED_CLASS_POSITION}
            />
          </div>
        )}

        {/* Month Selector */}
        <div
          style={{
            background: "linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)",
            borderRadius: "16px",
            border: "2px solid #e5e7eb",
            padding: "16px 20px",
            marginBottom: "20px",
            boxShadow: "0 4px 12px rgba(0, 0, 0, 0.08), 0 2px 4px rgba(0, 0, 0, 0.04)",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              marginBottom: "12px",
              gap: "12px",
              flexWrap: "wrap",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <i className="fa fa-calendar" style={{ fontSize: "14px", color: COLORS.primary_color_shade5 || "#3b82f6" }}></i>
              <h4
                style={{
                  margin: 0,
                  fontSize: "14px",
                  fontWeight: "700",
                  color: "#111827",
                }}
              >
                Select Month(s)
              </h4>
            </div>
            <div style={{ display: "flex", gap: "8px" }}>
              <label
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                  padding: "6px 12px",
                  fontSize: "12px",
                  fontWeight: "600",
                  color: "#f59e0b", // Orange color
                  cursor: "pointer",
                  transition: "all 0.2s ease",
                  whiteSpace: "nowrap",
                }}
              >
                <input
                  type="checkbox"
                  checked={setWorkingDaysAsDefault}
                  onChange={(e) => setSetWorkingDaysAsDefault(e.target.checked)}
                  style={{
                    width: "16px",
                    height: "16px",
                    cursor: "pointer",
                    accentColor: "#f59e0b", // Orange color
                  }}
                />
                <span>Set Working Days As Default</span>
              </label>
              <button
                onClick={handleSelectAllMonths}
                style={{
                  padding: "6px 12px",
                  fontSize: "12px",
                  fontWeight: "600",
                  borderRadius: "8px",
                  border: `2px solid ${COLORS.primary_color_shade4 || "#2563eb"}`,
                  background: "transparent",
                  color: COLORS.primary_color_shade4 || "#2563eb",
                  cursor: "pointer",
                  transition: "all 0.2s ease",
                }}
                onMouseEnter={(e) => {
                  e.target.style.background = `${COLORS.primary_color_shade5 || "#3b82f6"}15`;
                  e.target.style.borderColor = COLORS.primary_color_shade3 || "#1d4ed8";
                  e.target.style.color = COLORS.primary_color_shade3 || "#1d4ed8";
                  e.target.style.transform = "translateY(-1px)";
                  e.target.style.boxShadow = "0 4px 8px rgba(0, 0, 0, 0.15)";
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = "transparent";
                  e.target.style.borderColor = COLORS.primary_color_shade4 || "#2563eb";
                  e.target.style.color = COLORS.primary_color_shade4 || "#2563eb";
                  e.target.style.transform = "translateY(0)";
                  e.target.style.boxShadow = "none";
                }}
              >
                Select All
              </button>
              <button
                onClick={handleClearMonths}
                style={{
                  padding: "6px 12px",
                  fontSize: "12px",
                  fontWeight: "600",
                  borderRadius: "8px",
                  border: `2px solid ${COLORS.primary_color_shade4 || "#2563eb"}`,
                  background: "transparent",
                  color: COLORS.primary_color_shade4 || "#2563eb",
                  cursor: "pointer",
                  transition: "all 0.2s ease",
                }}
                onMouseEnter={(e) => {
                  e.target.style.background = `${COLORS.primary_color_shade5 || "#3b82f6"}15`;
                  e.target.style.borderColor = COLORS.primary_color_shade3 || "#1d4ed8";
                  e.target.style.color = COLORS.primary_color_shade3 || "#1d4ed8";
                  e.target.style.transform = "translateY(-1px)";
                  e.target.style.boxShadow = "0 4px 8px rgba(0, 0, 0, 0.15)";
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = "transparent";
                  e.target.style.borderColor = COLORS.primary_color_shade4 || "#2563eb";
                  e.target.style.color = COLORS.primary_color_shade4 || "#2563eb";
                  e.target.style.transform = "translateY(0)";
                  e.target.style.boxShadow = "none";
                }}
              >
                Clear All
              </button>
              <button
                onClick={() => {
                  // TODO: Implement Manage Working Days functionality
                }}
                style={{
                  padding: "6px 12px",
                  fontSize: "12px",
                  fontWeight: "600",
                  borderRadius: "8px",
                  border: `2px solid ${COLORS.primary_color_shade4 || "#2563eb"}`,
                  background: "transparent",
                  color: COLORS.primary_color_shade4 || "#2563eb",
                  cursor: "pointer",
                  transition: "all 0.2s ease",
                }}
                onMouseEnter={(e) => {
                  e.target.style.background = `${COLORS.primary_color_shade5 || "#3b82f6"}15`;
                  e.target.style.borderColor = COLORS.primary_color_shade3 || "#1d4ed8";
                  e.target.style.color = COLORS.primary_color_shade3 || "#1d4ed8";
                  e.target.style.transform = "translateY(-1px)";
                  e.target.style.boxShadow = "0 4px 8px rgba(0, 0, 0, 0.15)";
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = "transparent";
                  e.target.style.borderColor = COLORS.primary_color_shade4 || "#2563eb";
                  e.target.style.color = COLORS.primary_color_shade4 || "#2563eb";
                  e.target.style.transform = "translateY(0)";
                  e.target.style.boxShadow = "none";
                }}
              >
                Manage Working Days
              </button>
              <button
                onClick={() => {
                  setShowAttendancePreview(true);
                }}
                style={{
                  padding: "6px 12px",
                  fontSize: "12px",
                  fontWeight: "600",
                  borderRadius: "8px",
                  border: `2px solid ${COLORS.primary_color_shade4 || "#2563eb"}`,
                  background: "transparent",
                  color: COLORS.primary_color_shade4 || "#2563eb",
                  cursor: "pointer",
                  transition: "all 0.2s ease",
                }}
                onMouseEnter={(e) => {
                  e.target.style.background = `${COLORS.primary_color_shade5 || "#3b82f6"}15`;
                  e.target.style.borderColor = COLORS.primary_color_shade3 || "#1d4ed8";
                  e.target.style.color = COLORS.primary_color_shade3 || "#1d4ed8";
                  e.target.style.transform = "translateY(-1px)";
                  e.target.style.boxShadow = "0 4px 8px rgba(0, 0, 0, 0.15)";
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = "transparent";
                  e.target.style.borderColor = COLORS.primary_color_shade4 || "#2563eb";
                  e.target.style.color = COLORS.primary_color_shade4 || "#2563eb";
                  e.target.style.transform = "translateY(0)";
                  e.target.style.boxShadow = "none";
                }}
              >
                Attendance Preview
              </button>
            </div>
          </div>

          <div style={{ display: "flex", flexWrap: "wrap", gap: "10px" }}>
            {(workingDaysList.length > 0 ? workingDaysList : []).map((row) => {
              const isSelected = selectedMonths.includes(row.month);
              return (
                <label
                  key={row.month}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    padding: "8px 14px",
                    borderRadius: "10px",
                    background: isSelected
                      ? `linear-gradient(135deg, ${COLORS.primary_color_shade5 || "#3b82f6"}20 0%, ${COLORS.primary_color_shade5 || "#3b82f6"}10 100%)`
                      : "linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)",
                    border: isSelected
                      ? `2px solid ${COLORS.primary_color_shade5 || "#3b82f6"}`
                      : "2px solid #e5e7eb",
                    cursor: "pointer",
                    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                    fontSize: "13px",
                    fontWeight: isSelected ? "700" : "600",
                    color: isSelected ? COLORS.primary_color_shade5 || "#3b82f6" : "#64748b",
                    boxShadow: isSelected
                      ? `0 4px 12px ${(COLORS.primary_color_shade5 || "#3b82f6")}25, 0 2px 4px rgba(0, 0, 0, 0.1)`
                      : "0 2px 4px rgba(0, 0, 0, 0.05)",
                    transform: isSelected ? "translateY(-2px)" : "translateY(0)",
                  }}
                  onMouseEnter={(e) => {
                    if (!isSelected) {
                      e.currentTarget.style.background = "linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%)";
                      e.currentTarget.style.borderColor = "#cbd5e1";
                      e.currentTarget.style.transform = "translateY(-1px)";
                      e.currentTarget.style.boxShadow = "0 4px 8px rgba(0, 0, 0, 0.1)";
                    } else {
                      e.currentTarget.style.transform = "translateY(-3px)";
                      e.currentTarget.style.boxShadow = `0 6px 16px ${(COLORS.primary_color_shade5 || "#3b82f6")}30, 0 4px 8px rgba(0, 0, 0, 0.12)`;
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!isSelected) {
                      e.currentTarget.style.background = "linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)";
                      e.currentTarget.style.borderColor = "#e5e7eb";
                      e.currentTarget.style.transform = "translateY(0)";
                      e.currentTarget.style.boxShadow = "0 2px 4px rgba(0, 0, 0, 0.05)";
                    } else {
                      e.currentTarget.style.transform = "translateY(-2px)";
                      e.currentTarget.style.boxShadow = `0 4px 12px ${(COLORS.primary_color_shade5 || "#3b82f6")}25, 0 2px 4px rgba(0, 0, 0, 0.1)`;
                    }
                  }}
                >
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={() => handleMonthToggle(row.month)}
                    style={{
                      width: "16px",
                      height: "16px",
                      cursor: "pointer",
                      accentColor: COLORS.primary_color_shade5 || "#3b82f6",
                    }}
                  />
                  <span>
                    {row.month}{row.month2 ? `(${row.month2})` : ""}
                  </span>
                </label>
              );
            })}
          </div>
        </div>

        {/* Reports Table */}
        {loading ? (
          <div style={{ textAlign: "center", padding: "40px" }}>
            <MyLoader />
          </div>
        ) : filteredReports.length === 0 ? (
          <EmptyState message="No attendance reports found" />
        ) : (
          <>
            <div
              style={{
                overflowX: "auto",
                borderRadius: "16px",
                boxShadow: "0 4px 12px rgba(0, 0, 0, 0.08), 0 2px 4px rgba(0, 0, 0, 0.04)",
                border: "1px solid #e5e7eb",
                background: "#ffffff",
              }}
            >
              <table
                style={{
                  width: "100%",
                  borderCollapse: "separate",
                  borderSpacing: 0,
                  margin: 0,
                }}
              >
                <thead>
                  <tr
                    style={{
                      background: `linear-gradient(135deg, ${COLORS.primary_color_shade5 || "#3b82f6"} 0%, ${COLORS.primary_color_shade4 || "#2563eb"} 100%)`,
                      borderBottom: `3px solid ${COLORS.primary_color_shade4 || "#2563eb"}`,
                    }}
                  >
                    <th
                      style={{
                        padding: "14px 16px",
                        textAlign: "left",
                        fontSize: "12px",
                        fontWeight: "700",
                        color: "#ffffff",
                        textTransform: "uppercase",
                        letterSpacing: "0.5px",
                        width: "60px",
                      }}
                    >
                      SN
                    </th>
                    <th
                      style={{
                        padding: "14px 16px",
                        textAlign: "left",
                        fontSize: "12px",
                        fontWeight: "700",
                        color: "#ffffff",
                        textTransform: "uppercase",
                        letterSpacing: "0.5px",
                        minWidth: "220px",
                      }}
                    >
                      Student Name
                    </th>
                    <th
                      style={{
                        padding: "14px 16px",
                        textAlign: "left",
                        fontSize: "12px",
                        fontWeight: "700",
                        color: "#ffffff",
                        textTransform: "uppercase",
                        letterSpacing: "0.5px",
                        width: "100px",
                      }}
                    >
                      Roll No
                    </th>
                    {selectedMonths.map((m) => (
                      <th
                        key={m}
                        style={{
                          padding: "14px 16px",
                          textAlign: "center",
                          fontSize: "12px",
                          fontWeight: "700",
                          color: "#ffffff",
                          textTransform: "uppercase",
                          letterSpacing: "0.5px",
                          minWidth: "140px",
                        }}
                      >
                        {getHeaderLabelForMonth(m)}
                      </th>
                    ))}
                    <th
                      style={{
                        padding: "14px 16px",
                        textAlign: "center",
                        fontSize: "12px",
                        fontWeight: "700",
                        color: "#ffffff",
                        textTransform: "uppercase",
                        letterSpacing: "0.5px",
                        width: "120px",
                      }}
                    >
                      Save
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {filteredReports.map((report, index) => {
                    const studentId = report.student_id || report.id;
                    const isSaving = savingStudents[studentId];
                    return (
                      <tr
                        key={studentId || index}
                        style={{
                          background: index % 2 === 0 ? "#ffffff" : "#f8fafc",
                          transition: "background 0.2s ease",
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background = "#f1f5f9";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = index % 2 === 0 ? "#ffffff" : "#f8fafc";
                        }}
                      >
                        <td
                          style={{
                            padding: "14px 16px",
                            fontSize: "13px",
                            fontWeight: "700",
                            color: COLORS.primary_color_shade5 || "#3b82f6",
                            borderRight: "1px solid #e5e7eb",
                          }}
                        >
                          {index + 1}
                        </td>
                        <td
                          style={{
                            padding: "14px 16px",
                            fontSize: "14px",
                            fontWeight: "600",
                            color: "#111827",
                            borderRight: "1px solid #e5e7eb",
                          }}
                        >
                          {report.student_name || report.student_id || "N/A"}
                        </td>
                        <td
                          style={{
                            padding: "14px 16px",
                            fontSize: "13px",
                            fontWeight: "700",
                            color: COLORS.primary_color_shade5 || "#3b82f6",
                            borderRight: "1px solid #e5e7eb",
                          }}
                        >
                          {report.student_roll_no || "-"}
                        </td>
                        {selectedMonths.map((m) => {
                          // Check if there's an existing report with a present value
                          const existingReport = reports.find(
                            (r) => r.student_id === studentId && (r.month || "").trim() === m.trim()
                          );
                          const hasExistingValue = existingReport && 
                            existingReport.present !== null && 
                            existingReport.present !== undefined && 
                            existingReport.present !== "";
                          
                          // Check if this is a default input
                          const isDefault = defaultInputs[studentId] && defaultInputs[studentId][m];

                          return (
                            <td
                              key={m}
                              style={{
                                padding: "14px 16px",
                                borderRight: "1px solid #e5e7eb",
                              }}
                            >
                              <AttendanceInputCell
                                value={
                                  (presentInputs[studentId] &&
                                    presentInputs[studentId][m]) ??
                                  ""
                                }
                                onChange={(e) =>
                                  handlePresentInputChange(studentId, m, e.target.value)
                                }
                                placeholder="Present"
                                isExistingRecord={hasExistingValue}
                                isDefaultInput={isDefault}
                              />
                            </td>
                          );
                        })}
                        <td style={{ padding: "14px 16px", textAlign: "center", borderRight: "none" }}>
                          <button
                            onClick={() => handleSaveRow(report)}
                            disabled={isSaving || selectedMonths.length === 0}
                            style={{
                              ...STYLES.button,
                              padding: "8px 16px",
                              fontSize: "12px",
                              borderRadius: "10px",
                              background: isSaving || selectedMonths.length === 0
                                ? "#9ca3af"
                                : `linear-gradient(135deg, ${COLORS.primary_color_shade5 || "#3b82f6"} 0%, ${COLORS.primary_color_shade5 || "#2563eb"} 100%)`,
                              color: "#ffffff",
                              justifyContent: "center",
                              minWidth: "90px",
                              boxShadow: isSaving || selectedMonths.length === 0
                                ? "none"
                                : `0 4px 12px ${(COLORS.primary_color_shade5 || "#3b82f6")}30`,
                            }}
                            onMouseEnter={(e) => {
                              if (!isSaving && selectedMonths.length > 0) {
                                e.target.style.transform = "translateY(-2px) scale(1.05)";
                                e.target.style.boxShadow = `0 6px 16px ${(COLORS.primary_color_shade5 || "#3b82f6")}40`;
                              }
                            }}
                            onMouseLeave={(e) => {
                              if (!isSaving && selectedMonths.length > 0) {
                                e.target.style.transform = "translateY(0) scale(1)";
                                e.target.style.boxShadow = `0 4px 12px ${(COLORS.primary_color_shade5 || "#3b82f6")}30`;
                              }
                            }}
                          >
                            {isSaving ? (
                              <>
                                <i className="fa fa-spinner fa-spin" style={{ fontSize: "10px" }}></i>
                                Saving...
                              </>
                            ) : (
                              <>
                                <i className="fa fa-save" style={{ fontSize: "10px" }}></i>
                                Save
                              </>
                            )}
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <SaveAllButton position="bottom" />
          </>
        )}
      </div>

      {/* Attendance Preview Modal */}
      <style>
        {`
          .attendance-preview-modal .modal-content {
            border-radius: 12px;
            overflow: hidden;
            border: none;
          }
          .attendance-preview-modal .modal-dialog {
            margin: 1.75rem auto;
            max-width: 95vw;
          }
        `}
      </style>
      <Modal
        show={showAttendancePreview}
        onHide={() => setShowAttendancePreview(false)}
        size="xl"
        centered
        animation={false}
        dialogClassName="attendance-preview-modal"
        style={{ maxWidth: "95vw" }}
      >
        <Modal.Header
          style={{
            background: `linear-gradient(135deg, ${COLORS.primary_color_shade5 || "#3b82f6"} 0%, ${COLORS.primary_color_shade4 || "#2563eb"} 100%)`,
            color: "#ffffff",
            borderBottom: "none",
            borderRadius: "0",
            borderTopLeftRadius: "12px",
            borderTopRightRadius: "12px",
            overflow: "hidden",
            padding: "16px 20px",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%", flexWrap: "wrap", gap: "12px" }}>
            <Modal.Title
              style={{
                fontSize: "18px",
                fontWeight: "700",
                display: "flex",
                alignItems: "center",
                gap: "10px",
                margin: 0,
              }}
            >
              <i className="fa fa-eye"></i>
              Attendance Preview
            </Modal.Title>
            {(() => {
              if (!workingDaysList || workingDaysList.length === 0 || !filteredReports || filteredReports.length === 0) {
                return null;
              }
              const stats = calculateAttendanceStats();
              const allMonths = workingDaysList.map((r) => r.month).filter(Boolean);
              return (
                <div style={{ display: "flex", gap: "16px", alignItems: "center", flexWrap: "wrap" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                    <span style={{ fontSize: "12px", opacity: 0.9 }}>Completed:</span>
                    <span style={{ fontSize: "16px", fontWeight: "700" }}>{stats.completedCount}</span>
                    <span style={{ fontSize: "11px", opacity: 0.8 }}>/ {stats.totalExpected}</span>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                    <span style={{ fontSize: "12px", opacity: 0.9 }}>Not Completed:</span>
                    <span style={{ fontSize: "16px", fontWeight: "700" }}>{stats.notCompletedCount}</span>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                    <span style={{ fontSize: "12px", opacity: 0.9 }}>Students:</span>
                    <span style={{ fontSize: "16px", fontWeight: "700" }}>{filteredReports.length}</span>
                    <span style={{ fontSize: "11px", opacity: 0.8 }}>({allMonths.length} months)</span>
                  </div>
                </div>
              );
            })()}
            <Button
              variant="link"
              onClick={() => setShowAttendancePreview(false)}
              style={{
                color: "#ffffff",
                fontSize: "24px",
                fontWeight: "700",
                textDecoration: "none",
                padding: "0",
                lineHeight: "1",
                opacity: 0.9,
                marginLeft: "auto",
              }}
            >
              &times;
            </Button>
          </div>
        </Modal.Header>
        <Modal.Body style={{ padding: "20px", maxHeight: "70vh", overflowY: "auto" }}>
          {(() => {
            if (!workingDaysList || workingDaysList.length === 0 || !filteredReports || filteredReports.length === 0) {
              return (
                <div style={{ textAlign: "center", padding: "40px", color: "#6b7280" }}>
                  <i className="fa fa-info-circle" style={{ fontSize: "48px", marginBottom: "16px", opacity: 0.5 }}></i>
                  <p style={{ fontSize: "14px", fontWeight: "600" }}>No attendance data available</p>
                </div>
              );
            }
            const previewData = getAttendancePreviewData();
            const allMonths = workingDaysList.map((r) => r.month).filter(Boolean);

            return (
              <>
                {/* Attendance Table */}
                <div
                  style={{
                    overflowX: "auto",
                    borderRadius: "12px",
                    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
                    border: "1px solid #e5e7eb",
                  }}
                >
                  <table
                    style={{
                      width: "100%",
                      borderCollapse: "separate",
                      borderSpacing: 0,
                      margin: 0,
                    }}
                  >
                    <thead>
                      <tr
                        style={{
                          background: `linear-gradient(135deg, ${COLORS.primary_color_shade5 || "#3b82f6"} 0%, ${COLORS.primary_color_shade4 || "#2563eb"} 100%)`,
                        }}
                      >
                        <th
                          style={{
                            padding: "10px 8px",
                            textAlign: "left",
                            fontSize: "10px",
                            fontWeight: "700",
                            color: "#ffffff",
                            textTransform: "uppercase",
                            letterSpacing: "0.5px",
                            position: "sticky",
                            left: 0,
                            background: `linear-gradient(135deg, ${COLORS.primary_color_shade5 || "#3b82f6"} 0%, ${COLORS.primary_color_shade4 || "#2563eb"} 100%)`,
                            zIndex: 10,
                            minWidth: "32px",
                            width: "32px",
                          }}
                        >
                          SN
                        </th>
                        <th
                          style={{
                            padding: "10px 8px",
                            textAlign: "left",
                            fontSize: "10px",
                            fontWeight: "700",
                            color: "#ffffff",
                            textTransform: "uppercase",
                            letterSpacing: "0.5px",
                            position: "sticky",
                            left: "32px",
                            background: `linear-gradient(135deg, ${COLORS.primary_color_shade5 || "#3b82f6"} 0%, ${COLORS.primary_color_shade4 || "#2563eb"} 100%)`,
                            zIndex: 10,
                            minWidth: "130px",
                            width: "130px",
                          }}
                        >
                          Student Name
                        </th>
                        <th
                          style={{
                            padding: "10px 8px",
                            textAlign: "left",
                            fontSize: "10px",
                            fontWeight: "700",
                            color: "#ffffff",
                            textTransform: "uppercase",
                            letterSpacing: "0.5px",
                            minWidth: "55px",
                            width: "55px",
                          }}
                        >
                          Roll No
                        </th>
                        {allMonths.map((month) => {
                          const monthInfo = workingDaysList.find((r) => r.month === month);
                          const monthAbbr = abbreviateMonth(month);
                          const month2 = monthInfo?.month2 || "";
                          const workingDays = monthInfo?.working_days || 0;
                          return (
                            <th
                              key={month}
                              style={{
                                padding: "10px 8px",
                                textAlign: "center",
                                fontSize: "10px",
                                fontWeight: "700",
                                color: "#ffffff",
                                textTransform: "uppercase",
                                letterSpacing: "0.5px",
                                minWidth: "68px",
                                width: "68px",
                              }}
                            >
                              {monthAbbr}
                              {month2 && (
                                <>
                                  <br />
                                  <span style={{ fontSize: "8px", opacity: 0.8 }}>{month2}</span>
                                </>
                              )}
                              <br />
                              <span style={{ fontSize: "8px", opacity: 0.9 }}>({workingDays}d)</span>
                            </th>
                          );
                        })}
                        <th
                          style={{
                            padding: "10px 8px",
                            textAlign: "center",
                            fontSize: "10px",
                            fontWeight: "700",
                            color: "#ffffff",
                            textTransform: "uppercase",
                            letterSpacing: "0.5px",
                            minWidth: "70px",
                            width: "70px",
                            background: `linear-gradient(135deg, ${COLORS.primary_color_shade3 || "#1d4ed8"} 0%, ${COLORS.primary_color_shade4 || "#2563eb"} 100%)`,
                          }}
                        >
                          Total
                          <br />
                          <span style={{ fontSize: "8px", opacity: 0.9 }}>
                            ({(() => {
                              const totalWorkingDays = allMonths.reduce((sum, month) => {
                                const monthInfo = workingDaysList.find((r) => r.month === month);
                                return sum + (monthInfo?.working_days || 0);
                              }, 0);
                              return totalWorkingDays;
                            })()}d)
                          </span>
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {previewData.map((student, index) => (
                        <tr
                          key={student.student_id}
                          style={{
                            background: index % 2 === 0 ? "#f9fafb" : "#f3f4f6",
                          }}
                        >
                          <td
                            style={{
                              padding: "10px 8px",
                              fontSize: "11px",
                              fontWeight: "700",
                              color: COLORS.primary_color_shade5 || "#3b82f6",
                              borderRight: "1px solid #e5e7eb",
                              position: "sticky",
                              left: 0,
                              background: index % 2 === 0 ? "#f9fafb" : "#f3f4f6",
                              zIndex: 5,
                            }}
                          >
                            {index + 1}
                          </td>
                          <td
                            style={{
                              padding: "10px 8px",
                              fontSize: "12px",
                              fontWeight: "600",
                              color: "#111827",
                              borderRight: "1px solid #e5e7eb",
                              position: "sticky",
                              left: "32px",
                              background: index % 2 === 0 ? "#f9fafb" : "#f3f4f6",
                              zIndex: 5,
                            }}
                          >
                            {student.student_name}
                          </td>
                          <td
                            style={{
                              padding: "10px 8px",
                              fontSize: "11px",
                              fontWeight: "700",
                              color: COLORS.primary_color_shade5 || "#3b82f6",
                              borderRight: "1px solid #e5e7eb",
                              background: index % 2 === 0 ? "#f9fafb" : "#f3f4f6",
                            }}
                          >
                            {student.student_roll_no}
                          </td>
                          {allMonths.map((month) => {
                            const monthData = student.months[month];
                            const isCompleted = monthData?.isCompleted || false;
                            const present = monthData?.present ?? "";
                            return (
                              <td
                                key={month}
                                style={{
                                  padding: "10px 8px",
                                  textAlign: "center",
                                  fontSize: "11px",
                                  fontWeight: "600",
                                  borderRight: "1px solid #e5e7eb",
                                  background: isCompleted
                                    ? "#ecfdf5"
                                    : "#fef2f2",
                                  color: isCompleted ? "#065f46" : "#991b1b",
                                }}
                              >
                                {present || "-"}
                                {isCompleted && (
                                  <i
                                    className="fa fa-check-circle"
                                    style={{
                                      marginLeft: "6px",
                                      fontSize: "14px",
                                      color: "#10b981",
                                    }}
                                  ></i>
                                )}
                              </td>
                            );
                          })}
                          <td
                            style={{
                              padding: "10px 8px",
                              textAlign: "center",
                              fontSize: "12px",
                              fontWeight: "700",
                              borderRight: "1px solid #e5e7eb",
                              background: index % 2 === 0 ? "#f9fafb" : "#f3f4f6",
                              color: COLORS.primary_color_shade5 || "#3b82f6",
                            }}
                          >
                            {(() => {
                              const total = allMonths.reduce((sum, month) => {
                                const monthData = student.months[month];
                                const present = monthData?.present ?? "";
                                const presentNum = parseFloat(present) || 0;
                                return sum + presentNum;
                              }, 0);
                              return total > 0 ? total : "-";
                            })()}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            );
          })()}
        </Modal.Body>
        <Modal.Footer style={{ borderTop: "1px solid #e5e7eb", padding: "12px 20px", borderRadius: "0", borderBottomLeftRadius: "12px", borderBottomRightRadius: "12px", overflow: "hidden" }}>
          <Button
            variant="secondary"
            onClick={() => setShowAttendancePreview(false)}
            style={{
              padding: "8px 20px",
              borderRadius: "8px",
              fontWeight: "600",
            }}
          >
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default ManageManualAttendance;
